import type { ODSDropdownSelectProps, ODSMenuListItemProps } from '@telekom-ods/react-ui-kit';
import { useField, useFormikContext } from 'formik';

import { ODSSelect } from '../../InputFields';

interface FormikSelectProps {
  name: string;
  label: string;
  helperText?: string;
  /** Override for a plain hint via helperText - ignored once a validation error takes over (see ODSSelect's mode doc). */
  mode?: 'error' | 'informative';
  menuItems?: ODSMenuListItemProps[];
  disabled?: boolean | string;
  onChange?: ODSDropdownSelectProps['onValueChange'];
}

export const FormikSelect = ({
  name,
  label,
  menuItems = [],
  disabled = false,
  onChange: propOnChange,
  helperText,
  mode,
}: FormikSelectProps) => {
  const [field, meta, helpers] = useField(name);
  const { values } = useFormikContext<Record<string, unknown>>();
  const isDisabled = typeof disabled === 'string' ? !values[disabled] : disabled;

  const handleChange = (selected: { id?: string; value?: string; label?: string }) => {
    helpers.setValue(selected.value);
    if (propOnChange) {
      propOnChange(selected);
    }
  };

  // A validation error (once touched) always wins over a plain hint, and
  // always displays in 'error' mode - the `mode` override only applies to
  // the plain-hint case.
  const displayHelperText = meta.touched ? meta.error : helperText;

  return (
    <ODSSelect
      style={{
        maxWidth: '100%',
      }}
      id={field.name}
      name={field.name}
      value={field.value as string | number}
      onChange={handleChange as ODSDropdownSelectProps['onValueChange']}
      onBlur={() => {
        helpers.setTouched(true);
      }}
      label={label}
      menuItems={menuItems}
      disabled={isDisabled}
      {...(displayHelperText && {
        helperText: displayHelperText,
      })}
      {...(!meta.touched && mode && { mode })}
    />
  );
};
